import { Severity, TableColumn } from '@ui/aos-lib';
import { EquipmentListingFieldName } from '../../equipment/devices/components/devices.models';
import {
  commodityTableIcon,
  SEARCH_HINTS
} from '../../../shared/components/listing-page/services/listing-page-model/listing-page-model.constants';
import { FilterModel } from '../../../shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { EventMonitorFilterName } from '../../network-monitoring/events-monitor/events-monitor.types';
import { SubscriptionSeverityColumnComponent } from './components/severity-column/sub-severity-column.component';
import { EditModalColumnComponent } from './components/edit-modal-column/edit-modal-column.component';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { TextTranslateColumnComponent } from 'src/app/shared/components/listing-table/components/text-translate-column.component';
import { IconColumnComponent } from 'src/app/shared/components/listing-table/components/icon-column.component';

export const SUBSCRIPTION_PHONE_NUMBER_REGEX = /^\+1\d{10}$/;

export enum SubscriptionManagementFieldName {
  SubscriptionID = 'SubscriptionId',
  SubscriptionName = 'Name',
  EventGroupName = 'EventGroup',
  EventName = 'EventName',
  Commodity = 'Commodity',
  ReportGroupUID = 'ReportGroupUID',
  Channel = 'Channel',
  Contact = 'Contact',
  Delivery = 'Delivery'
}

export const SEARCH_TYPES = [
  {
    text: TranslateRegistry.get.SEARCH.TYPE.ALL,
    value: 1,
    types: [
      SubscriptionManagementFieldName.Contact,
      SubscriptionManagementFieldName.EventName,
      SubscriptionManagementFieldName.SubscriptionName
    ],
    hintText: SEARCH_HINTS.SubscriptionNameOrEventNameOrContact
  },
  {
    text: TranslateRegistry.get.SEARCH.TYPE.CONTACT,
    value: 2,
    types: [SubscriptionManagementFieldName.Contact],
    hintText: SEARCH_HINTS.Contact
  },
  {
    text: TranslateRegistry.get.SEARCH.TYPE.EVENT_NAME,
    value: 3,
    types: [SubscriptionManagementFieldName.EventName],
    hintText: SEARCH_HINTS.EventName
  },
  {
    text: TranslateRegistry.get.SEARCH.TYPE.SUBSCRIPTION_NAME,
    value: 4,
    types: [SubscriptionManagementFieldName.SubscriptionName],
    hintText: SEARCH_HINTS.SubscriptionName
  }
];

export const SUBSCRIPTION_SEARCH_MAX_LENGTH = 64;
export const SUBSCRIPTION_SEARCH_MAX_WIDTH = 34.5;

export const eventGroupFilterModel: FilterModel = {
  title: TranslateRegistry.get.FILTER.TITLE.EVENT_GROUP,
  filterType: SubscriptionManagementFieldName.EventGroupName,
  selected: [],
  items: [],
  isMultiselect: true,
  width: 180
};

export const reportGroupFilterModel: FilterModel = {
  title: TranslateRegistry.get.FILTER.TITLE.REPORT_GROUP,
  filterType: SubscriptionManagementFieldName.ReportGroupUID,
  selected: [],
  items: [],
  isMultiselect: true,
  width: 180
};

export const severityFilterModel = {
  title: TranslateRegistry.get.FILTER.TITLE.SEVERITY,
  filterType: EventMonitorFilterName.Severity,
  items: [
    { text: TranslateRegistry.get.FILTER.SEVERITY.CRITICAL, value: Severity.Critical },
    { text: TranslateRegistry.get.FILTER.SEVERITY.SEVERE, value: Severity.Severe },
    { text: TranslateRegistry.get.FILTER.SEVERITY.WARNING, value: Severity.Warning },
    { text: TranslateRegistry.get.FILTER.SEVERITY.NOTICE, value: Severity.Notice }
  ],
  selected: [
    { text: TranslateRegistry.get.FILTER.SEVERITY.CRITICAL, value: Severity.Critical },
    { text: TranslateRegistry.get.FILTER.SEVERITY.SEVERE, value: Severity.Severe },
    { text: TranslateRegistry.get.FILTER.SEVERITY.WARNING, value: Severity.Warning },
    { text: TranslateRegistry.get.FILTER.SEVERITY.NOTICE, value: Severity.Notice }
  ],
  isMultiselect: true,
  isNonSortable: true,
  width: 160,
  isColored: true
};

export const commodityFilterModel = {
  title: TranslateRegistry.get.FILTER.TITLE.COMMODITY,
  items: [],
  filterType: EventMonitorFilterName.Commodity,
  selected: [],
  isMultiselect: true,
  width: 160
};

export const channelFilterModel: FilterModel = {
  title: TranslateRegistry.get.FILTER.TITLE.CHANNEL,
  filterType: SubscriptionManagementFieldName.Channel,
  selected: [
    { text: TranslateRegistry.get.FILTER.CHANNEL.EMAIL, value: 'Email' },
    { text: TranslateRegistry.get.FILTER.CHANNEL.SMS, value: 'SMS' }
  ],
  items: [
    { text: TranslateRegistry.get.FILTER.CHANNEL.EMAIL, value: 'Email' },
    { text: TranslateRegistry.get.FILTER.CHANNEL.SMS, value: 'SMS' }
  ],
  isMultiselect: true,
  width: 160
};

export const deliveryFilterModel: FilterModel = {
  title: TranslateRegistry.get.FILTER.TITLE.DELIVERY,
  filterType: SubscriptionManagementFieldName.Delivery,
  selected: [
    { text: TranslateRegistry.get.FILTER.DELIVERY.IMMEDIATE, value: 'Immediate' },
    { text: TranslateRegistry.get.FILTER.DELIVERY.SCHEDULED, value: 'Scheduled' }
  ],
  items: [
    { text: TranslateRegistry.get.FILTER.DELIVERY.IMMEDIATE, value: 'Immediate' },
    { text: TranslateRegistry.get.FILTER.DELIVERY.SCHEDULED, value: 'Scheduled' }
  ],
  isMultiselect: true,
  width: 160
};

export const tableConfig: TableColumn[] = [
  {
    field: 'subscriptionId',
    title: TranslateRegistry.get.SUBSCRIPTION_MANAGEMENT.TABLE.SUBSCRIPTION_ID,
    width: 128,
    sortable: true
  },
  {
    field: 'subscriptionName',
    title: TranslateRegistry.get.SUBSCRIPTION_MANAGEMENT.TABLE.SUBSCRIPTION_NAME,
    width: 236,
    sortable: true
  },
  {
    field: 'eventGroup',
    title: TranslateRegistry.get.SUBSCRIPTION_MANAGEMENT.TABLE.EVENT_GROUP,
    width: 158,
    sortable: false
  },
  {
    field: 'eventName',
    title: TranslateRegistry.get.SUBSCRIPTION_MANAGEMENT.TABLE.EVENT_NAME,
    width: 236,
    sortable: false
  },
  {
    field: 'severityRules',
    title: TranslateRegistry.get.SUBSCRIPTION_MANAGEMENT.TABLE.SEVERITY,
    width: 78,
    sortable: false,
    isRightAligned: true,
    cmp: SubscriptionSeverityColumnComponent
  },
  {
    field: 'commodity',
    title: TranslateRegistry.get.SUBSCRIPTION_MANAGEMENT.TABLE.COMMODITY,
    width: 112,
    sortable: false,
    icon: commodityTableIcon,
    cmp: IconColumnComponent
  },
  {
    field: 'reportGroup',
    title: TranslateRegistry.get.SUBSCRIPTION_MANAGEMENT.TABLE.REPORT_GROUP,
    width: 236,
    sortable: false
  },
  {
    field: 'channel',
    title: TranslateRegistry.get.SUBSCRIPTION_MANAGEMENT.TABLE.CHANNEL,
    width: 158,
    sortable: false,
    cmp: TextTranslateColumnComponent
  },
  { field: 'contact', title: TranslateRegistry.get.SUBSCRIPTION_MANAGEMENT.TABLE.CONTACT, width: 256, sortable: false },
  {
    field: 'delivery',
    title: TranslateRegistry.get.SUBSCRIPTION_MANAGEMENT.TABLE.DELIVERY,
    width: 236,
    sortable: false
  },
  {
    field: 'edit',
    isEdit: true,
    title: TranslateRegistry.get.SUBSCRIPTION_MANAGEMENT.TABLE.EDIT,
    sticky: true,
    width: 64,
    sortable: false,
    cmp: EditModalColumnComponent
  }
];

export const tableSortFiled: Record<string, EquipmentListingFieldName> = {
  subscriptionId: SubscriptionManagementFieldName.SubscriptionID,
  subscriptionName: SubscriptionManagementFieldName.SubscriptionName,
  eventGroupName: SubscriptionManagementFieldName.EventGroupName,
  eventName: SubscriptionManagementFieldName.EventName,
  channel: SubscriptionManagementFieldName.Channel,
  contact: SubscriptionManagementFieldName.Contact,
  delivery: SubscriptionManagementFieldName.Delivery
};
